import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import plusIcon from "../../../assets/img/icons/plus_white.svg";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
} from "../../../styledComponents/buttons";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputLabel,
  InputWithLabelSection,
} from "../../../styledComponents/common";
import { currencySymbolMap } from "../../../utils/currencies";
import LineItemModal from "../../JobComponents/LineItemModal";
import LineItemsTable from "../../JobComponents/LineItemsTable";
import { CommonCardContainer } from "components/DashboardCards/cardStyles";
import {
  GrandTotalAmount,
  GrandTotalAmountSection,
  GrandTotalContainer,
  GrandTotalLabel,
  GrandTotalLabelSection,
  TaxAmountLabel,
} from "../invoiceStyles";
import TaxAmountSection from "./TaxAmountSection";
import { InputField } from "../../../styledComponents/common";

const InvoiceLineItemSectionCreate = props => {
  const {
    setLineItems,
    setTaxDiscount,
    pageType,
    lineItems,
    currency,
    selectedVisitJobs,
    selectedJob,
    setGrandTotal,
  } = props;

  const { t } = useTranslation();
  const [action, setAction] = useState("add");
  const [popupOpen, setPopupOpen] = useState(false);
  const [dataForEdit, setDataForEdit] = useState(null);

  const [itemArray, setItemArray] = useState([]);
  const [totalCalc, setTotalCalc] = useState(0);
  const [tax1, setTax1] = useState({
    name: "",
    value: "",
    percent: true,
  });
  const [tax2, setTax2] = useState({
    name: "",
    value: "",
    percent: true,
  });
  const [discount, setDiscount] = useState({
    value: "",
    percent: true,
  });
  const [grandTotalValue, setGrandTotalValue] = useState(0);
  const [saveErrors, setSaveErrors] = useState(null);

  // Send item Array to Parent component when itemArray changes
  useEffect(() => {
    totalCalculation();

    if (itemArray?.length > 0) {
      const payload = itemArray.map((item, index) => ({
        item_type: item.itemType?.toLowerCase(),
        description: item.description,
        serial_number: index + 1,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      }));
      setLineItems(payload);
    }
  }, [itemArray]);

  // Calculate grand total when calculations or items change
  useEffect(() => {
    grandTotalCalculation();
  }, [itemArray, tax1.value, tax1.percent, tax2.value, tax2.percent, discount.value, discount.percent, totalCalc]);

  // Populate line items from selected visit jobs' subtasks
  useEffect(() => {
    // Keep manually added items (items without subtaskId)
    const manualItems = itemArray.filter(item => !item.subtaskId);
    const subtaskItems = [];

    // Process selectedJob (single job)
    if (selectedJob && selectedJob.value) {
      if (selectedJob.subtasks && selectedJob.subtasks.length > 0) {
        selectedJob.subtasks.forEach(subtask => {
          subtaskItems.push({
            itemType: "Service",
            name: subtask.name,
            description: subtask.description || "",
            quantity: subtask.quantity,
            unitPrice: subtask.unit_price,
            amount: subtask.quantity * subtask.unit_price,
            evidenceToggle: false,
            tax1Toggle: true,
            tax2Toggle: true,
            subtaskId: subtask.id,
            jobId: selectedJob.value,
          });
        });
      }
    }

    // Process selectedVisitJobs (recurring jobs with multiple dates)
    if (selectedVisitJobs && selectedVisitJobs.length > 0) {
      selectedVisitJobs.forEach(job => {
        if (job.subtasks && job.subtasks.length > 0) {
          job.subtasks.forEach(subtask => {
            subtaskItems.push({
              itemType: "Service",
              name: subtask.name,
              description: subtask.description || "",
              quantity: subtask.quantity,
              unitPrice: subtask.unit_price,
              amount: subtask.quantity * subtask.unit_price,
              evidenceToggle: false,
              tax1Toggle: true,
              tax2Toggle: true,
              subtaskId: subtask.id,
              jobId: job.id,
            });
          });
        }
      });
    }

    // Combine manual items with subtask items
    if (subtaskItems.length > 0) {
      setItemArray([...manualItems, ...subtaskItems]);
    } else {
      // When no jobs are selected, keep only manual items
      setItemArray(manualItems);
    }
  }, [selectedJob, selectedVisitJobs]);

  const handleDeleteItem = index => {
    let newArray = [...itemArray];
    newArray.splice(index, 1);
    setItemArray(newArray);
  };

  const totalCalculation = () => {
    if (itemArray && itemArray.length) {
      let total = 0;
      itemArray.forEach(item => {
        total += item.quantity * item.unitPrice;
      });
      setTotalCalc(total.toFixed(2));
    } else {
      setTotalCalc(0);
    }
  };

  const handleEditItem = index => {
    let item = itemArray[index];
    setDataForEdit({ ...item, index });
    setAction("edit");
    setPopupOpen(true);
  };

  const handleAddItems = items => {
    // Handle Edit Case
    if (items.action === "edit") {
      let newArray = [...itemArray];
      newArray[items.index] = {
        itemType: items.itemType,
        name: items.name,
        description: items.description,
        quantity: items.quantity,
        unitPrice: items.unitPrice,
        amount: items.amount,
        evidenceToggle: items.evidenceToggle,
        tax1Toggle: items.tax1Toggle,
        tax2Toggle: items.tax2Toggle,
        id: items.id,
        subtaskId: items.subtaskId,
        jobId: items.jobId,
      };
      setItemArray(newArray);
      return;
    }

    // Handle Add Case
    let newArray = [...itemArray, items];
    setItemArray(newArray);
  };

  const handleTax1ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax1Toggle = !newArr[index].tax1Toggle;
    setItemArray(newArr);
  };

  const handleTax2ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax2Toggle = !newArr[index].tax2Toggle;
    setItemArray(newArr);
  };

  const onTax1NameChange = e => {
    const updated = { ...tax1, name: e.target.value };
    setTax1(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1: updated, tax2, discount });
    }
  };

  const onTax1ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax1Value`];
    setSaveErrors(errors);

    const updated = { ...tax1, value: e.target.value };
    setTax1(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1: updated, tax2, discount });
    }
  };

  const toggleTax1CalcType = value => {
    const updated = {
      ...tax1,
      percent: value,
    };
    setTax1(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1: updated, tax2, discount });
    }
  };

  const onTax2NameChange = e => {
    const updated = { ...tax2, name: e.target.value };
    setTax2(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1, tax2: updated, discount });
    }
  };

  const onTax2ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax2Value`];
    setSaveErrors(errors);

    const updated = { ...tax2, value: e.target.value };
    setTax2(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1, tax2: updated, discount });
    }
  };

  const toggleTax2CalcType = value => {
    const updated = {
      ...tax2,
      percent: value,
    };
    setTax2(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1, tax2: updated, discount });
    }
  };

  const onDiscountValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`discountValue`];
    delete errors[`discountFixed`];
    setSaveErrors(errors);

    const updated = { ...discount, value: e.target.value };
    setDiscount(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1, tax2, discount: updated });
    }
  };

  const toggleDiscountCalcType = value => {
    const updated = {
      ...discount,
      percent: value,
    };
    setDiscount(updated);
    if (setTaxDiscount) {
      setTaxDiscount({ tax1, tax2, discount: updated });
    }
  };

  const grandTotalCalculation = () => {
    const calculateTaxDiscount = (object, cost) => {
      if (object.percent) {
        return (object.value * cost) / 100;
      } else if (object.value) {
        return object.value;
      } else {
        return 0;
      }
    };

    if (itemArray && itemArray.length) {
      let totalTax = 0;
      itemArray.forEach((item, index) => {
        let itemCost = item.quantity * item.unitPrice;
        if (item.tax1Toggle) {
          totalTax += parseFloat(calculateTaxDiscount(tax1, itemCost));
        }
        if (item.tax2Toggle) {
          totalTax += parseFloat(calculateTaxDiscount(tax2, itemCost));
        }
      });

      let grandTotal =
        parseFloat(totalCalc) +
        totalTax -
        parseFloat(calculateTaxDiscount(discount, totalCalc));
      setGrandTotalValue(grandTotal.toFixed(2));
      if (setGrandTotal) {
        setGrandTotal(grandTotal.toFixed(2));
      }
    }
  };

  const handleAddItemClick = () => {
    setAction("add");
    setDataForEdit(null);
    setPopupOpen(true);
  };

  return (
    <CommonCardContainer style={{ minWidth: '1200px', marginTop: "6px" }} bgColor="#EDF8FE">
      <LineItemModal
        isOpen={popupOpen}
        toggle={() => setPopupOpen(!popupOpen)}
        addItems={handleAddItems}
        action={action}
        dataForEdit={dataForEdit}
        currency={currency}
        pageType="invoice"
      />
      <CommonFlex disableResponsive margin="0 0 16px 0" justifyContent="space-between" alignItems="center">
        <CommonText name title fontSize="16px">
          Line Items
        </CommonText>
        <ColoredButtonWithIcon
          type="orange"
          onClick={() => handleAddItemClick()}
        >
          <ButtonIcon src={plusIcon} alt="plus" />
          Add Line Items
        </ColoredButtonWithIcon>
      </CommonFlex>

      <div>
        {itemArray && itemArray.length > 0 ? (
          <LineItemsTable
            itemArray={itemArray}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onTax1ToggleClick={handleTax1ToggleClick}
            onTax2ToggleClick={handleTax2ToggleClick}
            totalCalc={totalCalc}
            currency={currency}
            pageType="invoice"
          />
        ) : (
          <CommonText>No line items added yet</CommonText>
        )}
      </div>

      <CommonFlex disableResponsive justifyContent="flex-end" style={{ marginTop: "20px" }} alignItems="center">
        <CommonFlex disableResponsive width="418px" direction="column" alignItems="flex-end" gap="16px">
          <CommonGrid>
            <InputWithLabelSection>
              <TaxAmountLabel>Tax 1 Name</TaxAmountLabel>
              <InputField
                type="text"
                placeholder="Enter Tax 1 Name"
                value={tax1.name}
                onChange={e => onTax1NameChange(e)}
              />
            </InputWithLabelSection>
            <TaxAmountSection
              value={tax1.value}
              onChange={e => onTax1ValueChange(e)}
              isPercentage={tax1.percent}
              onTypeChange={isPercent => toggleTax1CalcType(isPercent)}
              currency={currency}
            />
          </CommonGrid>
          <CommonGrid disableResponsive>
            <InputWithLabelSection>
              <TaxAmountLabel>Tax 2 Name</TaxAmountLabel>
              <InputField
                type="text"
                placeholder="Enter Tax 2 Name"
                value={tax2.name}
                onChange={e => onTax2NameChange(e)}
              />
            </InputWithLabelSection>
            <TaxAmountSection
              value={tax2.value}
              onChange={e => onTax2ValueChange(e)}
              isPercentage={tax2.percent}
              onTypeChange={isPercent => toggleTax2CalcType(isPercent)}
              currency={currency}
            />
          </CommonGrid>
          <TaxAmountSection
            label="Discount"
            value={discount.value}
            onChange={e => onDiscountValueChange(e)}
            isPercentage={discount.percent}
            onTypeChange={isPercent => toggleDiscountCalcType(isPercent)}
            currency={currency}
          />
          <GrandTotalContainer>
            <GrandTotalLabelSection>
              <GrandTotalLabel>Grand Total</GrandTotalLabel>
            </GrandTotalLabelSection>
            <GrandTotalAmountSection>
              <GrandTotalAmount>
                {currency && currencySymbolMap[currency] ? currencySymbolMap[currency] : '$'}
                {grandTotalValue || "0.00"}
              </GrandTotalAmount>
            </GrandTotalAmountSection>
          </GrandTotalContainer>
        </CommonFlex>
      </CommonFlex>
    </CommonCardContainer>
  );
};

export default InvoiceLineItemSectionCreate;
